import { CHANNELS, LANGUAGES, policyholderPatchSchema } from '@beta-assur/shared';
import { Link, useParams } from '@tanstack/react-router';
import { useState, type FormEvent, type ReactNode } from 'react';
import { ContractStatusBadge } from '../components/badges';
import { Facts, Field, PageHeader } from '../components/page';
import { ActionError, Empty, QueryView } from '../components/states';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input, Select } from '../components/ui/input';
import { Table, TBody, TD, TH, THead, TR } from '../components/ui/table';
import { useAuth } from '../lib/auth';
import { day, instant } from '../lib/format';
import { useI18n } from '../lib/i18n';
import { useApiMutation, useApiQuery } from '../lib/query';
import { useReferentials, useTenant } from '../lib/referentials';
import type { ContractPage, MessagePage, Policyholder } from '../lib/types';
import { ContractCreateDialog, fieldErrors } from './portfolio-forms';
import { PolicyholderRightsCard } from './policyholder-rights';

// Fiche assuré (BA-023, spec 08 §3) : contact et préférences modifiables, contrats, messages.

/** Emplacement d'actions supplémentaires (vérification du numéro, droits : BA-043, BA-044). */
export type PolicyholderActions = (holder: Policyholder) => ReactNode;

function ContactForm({ holder }: { holder: Policyholder }) {
  const t = useI18n().t;
  const initial = {
    phone: holder.phone ?? '',
    email: holder.email ?? '',
    preferredChannel: holder.preferredChannel,
    language: holder.language,
  };
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState(false);
  const save = useApiMutation<unknown>('PATCH', `/v1/policyholders/${holder.id}`, {
    invalidate: ['/v1/policyholders', '/v1/installments'],
    onSuccess: () => setSaved(true),
  });
  const set = (key: keyof typeof form) => (e: { target: { value: string } }) => {
    setSaved(false);
    setForm((f) => ({ ...f, [key]: e.target.value }));
  };

  function submit(event: FormEvent) {
    event.preventDefault();
    const parsed = policyholderPatchSchema.safeParse({
      phone: form.phone.trim() || null,
      email: form.email.trim() || null,
      preferredChannel: form.preferredChannel,
      language: form.language,
    });
    if (!parsed.success) return setErrors(fieldErrors(parsed.error.issues));
    setErrors({});
    save.mutate(parsed.data);
  }

  return (
    <form onSubmit={submit} className="grid grid-cols-1 gap-3 sm:grid-cols-2" noValidate>
      <Field label={t.portfolio.phone} error={errors.phone}>
        <Input type="tel" dir="ltr" value={form.phone} onChange={set('phone')} />
      </Field>
      <Field label={t.portfolio.email} error={errors.email}>
        <Input type="email" value={form.email} onChange={set('email')} />
      </Field>
      <Field label={t.portfolio.preferredChannel}>
        <Select value={form.preferredChannel} onChange={set('preferredChannel')}>
          {CHANNELS.map((c) => (
            <option key={c} value={c}>
              {t.channels[c]}
            </option>
          ))}
        </Select>
      </Field>
      <Field label={t.portfolio.language}>
        <Select value={form.language} onChange={set('language')}>
          {LANGUAGES.map((l) => (
            <option key={l} value={l}>
              {t.languages[l]}
            </option>
          ))}
        </Select>
      </Field>
      <div className="flex items-center gap-3 sm:col-span-2">
        <Button type="submit" disabled={save.isPending}>
          {t.common.save}
        </Button>
        {saved ? (
          <span role="status" className="text-sm text-success">
            {t.common.saved}
          </span>
        ) : null}
        <ActionError error={save.error} />
      </div>
    </form>
  );
}

export function PolicyholderDetailPage({ actions }: { actions?: PolicyholderActions } = {}) {
  const { t, language } = useI18n();
  const auth = useAuth();
  const { id } = useParams({ strict: false }) as { id: string };
  const refs = useReferentials();
  const tz = useTenant().data?.timezone;
  const query = useApiQuery<Policyholder>(`/v1/policyholders/${id}`);
  const contracts = useApiQuery<ContractPage>('/v1/contracts', { policyholderId: id, limit: 100 });
  const messages = useApiQuery<MessagePage>(auth.can('administer') ? '/v1/messages' : null, {
    policyholderId: id,
    limit: 50,
  });
  const own = messages.data?.items ?? [];
  const editable = auth.can('editPortfolio');

  return (
    <QueryView query={query}>
      {(holder) => (
        <>
          <PageHeader
            title={holder.displayName}
            subtitle={holder.externalRef ?? undefined}
            actions={editable ? <ContractCreateDialog policyholderId={holder.id} /> : null}
          />
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>{t.portfolio.contact}</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col gap-4">
                <Facts
                  items={[
                    [
                      t.portfolio.phone,
                      holder.phone ? (
                        <span dir="ltr">
                          {holder.phone}{' '}
                          {holder.phoneVerifiedAt ? (
                            <Badge tone="success">{t.portfolio.phoneVerified}</Badge>
                          ) : null}
                        </span>
                      ) : null,
                    ],
                    [t.portfolio.email, holder.email],
                    [t.portfolio.cin, holder.cin],
                    [
                      t.portfolio.optOut,
                      holder.optOutAt
                        ? `${holder.optOutChannels.map((c) => t.channels[c]).join(', ')} ${t.portfolio.optOutSince} ${day(holder.optOutAt, language)}`
                        : t.portfolio.noOptOut,
                    ],
                  ]}
                />
                {editable ? <ContactForm key={JSON.stringify(holder)} holder={holder} /> : null}
                {actions ? actions(holder) : null}
              </CardContent>
            </Card>
            {editable ? (
              <PolicyholderRightsCard holder={holder} onChange={() => void query.refetch()} />
            ) : null}
            <Card>
              <CardHeader>
                <CardTitle>{t.portfolio.contractsOf}</CardTitle>
              </CardHeader>
              {!contracts.data || contracts.data.items.length === 0 ? (
                <Empty />
              ) : (
                <ul className="divide-y divide-border">
                  {contracts.data.items.map((c) => (
                    <li
                      key={c.id}
                      className="flex flex-wrap items-center justify-between gap-2 px-4 py-2 text-sm"
                    >
                      <Link
                        to="/contrats/$id"
                        params={{ id: c.id }}
                        className="font-medium text-primary hover:underline"
                      >
                        {c.policyNumber}
                      </Link>
                      <span className="text-muted-foreground">
                        {refs.productName(c.productLineId)}
                      </span>
                      <ContractStatusBadge status={c.status} />
                    </li>
                  ))}
                </ul>
              )}
            </Card>
            {auth.can('administer') ? (
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle>{t.portfolio.messages}</CardTitle>
                </CardHeader>
                {own.length === 0 ? (
                  <Empty>{t.portfolio.noMessages}</Empty>
                ) : (
                  <Table>
                    <THead>
                      <tr>
                        <TH>{t.scenarios.channel}</TH>
                        <TH>{t.installments.status}</TH>
                        <TH>{t.installments.nextRun}</TH>
                      </tr>
                    </THead>
                    <TBody>
                      {own.map((m) => (
                        <TR key={m.id}>
                          <TD>{t.channels[m.channel]}</TD>
                          <TD>{m.status}</TD>
                          <TD>{instant(m.sentAt, tz, language)}</TD>
                        </TR>
                      ))}
                    </TBody>
                  </Table>
                )}
              </Card>
            ) : null}
          </div>
        </>
      )}
    </QueryView>
  );
}
